/**
 * V10 Phase C — Graph@asOf fetch 훅.
 *
 * React Query 가 없으므로 경량 useState/useEffect 기반으로 구현.
 * - `keepPreviousData` 효과: 이전 결과를 유지하면서 백그라운드에서 새 asOf 로 갱신
 * - slider 연속 조작 시 debounce 200ms 로 서버 호출 줄임
 * - `enabled=false` 면 fetch 보류 (예: `isHydrated` 대기)
 */
import { useEffect, useRef, useState } from "react"
import type { AdminClient, GraphAtAsOfResponse } from "../api/admin-client.ts"

type UseGraphAtAsOfResult = {
	readonly data: GraphAtAsOfResponse | undefined
	readonly isLoading: boolean
	readonly error: string | undefined
	readonly refresh: () => void
}

export function useGraphAtAsOf(args: {
	readonly client: AdminClient
	readonly slug: string
	readonly asOf: string | undefined
	readonly enabled: boolean
	/** slider 연속 조작 debounce (ms). 기본 200ms. */
	readonly debounceMs?: number
}): UseGraphAtAsOfResult {
	const { client, slug, asOf, enabled, debounceMs = 200 } = args
	const [data, setData] = useState<GraphAtAsOfResponse | undefined>()
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [refreshSeq, setRefreshSeq] = useState(0)
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	// biome-ignore lint/correctness/useExhaustiveDependencies: refreshSeq 는 refresh() 재호출 트리거 플래그이므로 의도적으로 의존성에 포함
	useEffect(() => {
		if (!enabled) return
		if (timerRef.current) clearTimeout(timerRef.current)
		let cancelled = false
		timerRef.current = setTimeout(() => {
			setIsLoading(true)
			setError(undefined)
			client
				.getGraphAtAsOf(slug, asOf)
				.then((res) => {
					if (!cancelled) setData(res)
				})
				.catch((e: Error) => {
					if (!cancelled) setError(e.message)
				})
				.finally(() => {
					if (!cancelled) setIsLoading(false)
				})
		}, debounceMs)
		return () => {
			cancelled = true
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [client, slug, asOf, enabled, debounceMs, refreshSeq])

	return {
		data,
		isLoading,
		error,
		refresh: () => setRefreshSeq((n) => n + 1)
	}
}
